import { API } from '../../../../../../../constants/config';
import translations from '../../../../../../../lang';
import { fetchJson, isAbortError } from '../../../../../../../utils/fetchJson';

import type {
  TrialInviteCredentials,
  TrialSignupFormValues,
} from '../schema/schema';
import { toTrialRegisterPayload } from '../schema/schema';

import { TRIAL_SIGNUP_ENDPOINTS } from './constants';

const { TRIAL_SIGNUP } = translations['en-US'];

type FetchJsonInit = NonNullable<Parameters<typeof fetchJson>[1]>;

export type TrialSignupValidResponse = {
  valid: boolean;
  message?: string;
};

export class TrialSignupApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TrialSignupApiError';
  }
}

const isTrialSignupValidResponse = (
  data: unknown
): data is TrialSignupValidResponse =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as TrialSignupValidResponse).valid === 'boolean' &&
  ((data as TrialSignupValidResponse).message === undefined ||
    typeof (data as TrialSignupValidResponse).message === 'string');

const fetchTrialSignupJson = async <T extends TrialSignupValidResponse>(
  url: string,
  init: FetchJsonInit
): Promise<T> => {
  const fallbackMessage =
    init.fallbackMessage ?? TRIAL_SIGNUP.ERRORS.EMAIL_VALIDATE_FAILED;

  try {
    const data = await fetchJson<T>(url, init);
    if (!isTrialSignupValidResponse(data))
      throw new TrialSignupApiError(fallbackMessage);

    return data as T;
  } catch (error) {
    if (isAbortError(error)) throw error;

    throw error instanceof TrialSignupApiError
      ? error
      : new TrialSignupApiError(fallbackMessage);
  }
};

export type ValidateTrialEmailResponse = {
  valid: boolean;
  message: string;
};

/**
 * Public GET — trial signup is unauthenticated; do not attach auth headers.
 */
export const validateTrialEmail = (
  email: string,
  signal?: AbortSignal
): Promise<ValidateTrialEmailResponse> =>
  fetchTrialSignupJson<ValidateTrialEmailResponse>(
    `${API.APP_SERVICES.BASE_URL}${
      TRIAL_SIGNUP_ENDPOINTS.VALIDATE_EMAIL
    }?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
      signal,
      fallbackMessage: TRIAL_SIGNUP.ERRORS.EMAIL_VALIDATE_FAILED,
    }
  );

export type VerifyTrialInvitePayload = {
  id: string;
  verification_code: string;
};

export type VerifyTrialInviteResponse = {
  valid: boolean;
};

/**
 * Public POST — trial invite link verification; do not attach auth headers.
 */
export const verifyTrialInvite = (
  payload: VerifyTrialInvitePayload,
  signal?: AbortSignal
): Promise<VerifyTrialInviteResponse> =>
  fetchTrialSignupJson<VerifyTrialInviteResponse>(
    `${API.BASE_URL}${TRIAL_SIGNUP_ENDPOINTS.VERIFY}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
      fallbackMessage: TRIAL_SIGNUP.ERRORS.VERIFY_FAILED,
    }
  );

export type TrialPanelOption = {
  label: string;
  value: string;
};

const isTrialOptionsMap = (data: unknown): data is Record<string, string> => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }

  return Object.entries(data).every(
    ([label, value]) =>
      typeof label === 'string' &&
      label.trim().length > 0 &&
      typeof value === 'string' &&
      value.trim().length > 0
  );
};

const toTrialOptions = (options: Record<string, string>): TrialPanelOption[] =>
  Object.entries(options).map(([label, value]) => ({ label, value }));

/**
 * Public GET — available trial target panels; do not attach auth headers.
 * Response is a label → value map, e.g. { "Discover US+": "affinity-sm-us-mri" }.
 */
export const fetchTrialPanels = async (
  signal?: AbortSignal
): Promise<TrialPanelOption[]> => {
  const fallbackMessage = TRIAL_SIGNUP.ERRORS.PANELS_LOAD_FAILED;

  try {
    const data = await fetchJson<unknown>(
      `${API.BASE_URL}${TRIAL_SIGNUP_ENDPOINTS.PANELS}`,
      {
        method: 'GET',
        signal,
        fallbackMessage,
      }
    );

    if (!isTrialOptionsMap(data)) {
      throw new TrialSignupApiError(fallbackMessage);
    }

    return toTrialOptions(data);
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    throw error instanceof TrialSignupApiError
      ? error
      : new TrialSignupApiError(fallbackMessage);
  }
};

export type TrialCountryOption = {
  label: string;
  value: string;
};

type TrialCountryApiItem = {
  name: string;
  code: string;
};

const isTrialCountryItem = (item: unknown): item is TrialCountryApiItem =>
  !!item &&
  typeof item === 'object' &&
  typeof (item as TrialCountryApiItem).name === 'string' &&
  (item as TrialCountryApiItem).name.trim().length > 0 &&
  typeof (item as TrialCountryApiItem).code === 'string' &&
  (item as TrialCountryApiItem).code.trim().length > 0;

const isTrialCountriesList = (
  data: unknown
): data is TrialCountryApiItem[] =>
  Array.isArray(data) && data.every(isTrialCountryItem);

const toTrialCountryOptions = (
  countries: TrialCountryApiItem[]
): TrialCountryOption[] =>
  countries.map(({ name, code }) => ({ label: name, value: code }));

/**
 * Public GET — available countries for trial signup; do not attach auth
 * headers. Response is an array of `{ name, code }`.
 */
export const fetchTrialCountries = async (
  signal?: AbortSignal
): Promise<TrialCountryOption[]> => {
  const fallbackMessage = TRIAL_SIGNUP.ERRORS.COUNTRIES_LOAD_FAILED;

  try {
    const data = await fetchJson<unknown>(
      `${API.BASE_URL}${TRIAL_SIGNUP_ENDPOINTS.COUNTRIES}`,
      {
        method: 'GET',
        signal,
        fallbackMessage,
      }
    );

    if (!isTrialCountriesList(data))
      throw new TrialSignupApiError(fallbackMessage);

    return toTrialCountryOptions(data);
  } catch (error) {
    if (isAbortError(error)) throw error;

    throw error instanceof TrialSignupApiError
      ? error
      : new TrialSignupApiError(fallbackMessage);
  }
};

export type TrialRegisterResponse = {
  message?: string;
  user_id?: number;
  company_id?: number;
};

const readErrorMessage = (data: unknown): string | undefined =>
  data &&
  typeof data === 'object' &&
  typeof (data as { errorMessage?: unknown }).errorMessage === 'string'
    ? (data as { errorMessage: string }).errorMessage
    : undefined;

const asTrialRegisterResponse = (data: unknown): TrialRegisterResponse => {
  if (!data || typeof data !== 'object') {
    return {};
  }

  const body = data as {
    message?: unknown;
    user_id?: unknown;
    company_id?: unknown;
  };

  return {
    ...(typeof body.message === 'string' ? { message: body.message } : {}),
    ...(typeof body.user_id === 'number' ? { user_id: body.user_id } : {}),
    ...(typeof body.company_id === 'number'
      ? { company_id: body.company_id }
      : {}),
  };
};

/**
 * Public POST — create trial account. Password is required for provisioning;
 * never log the payload. Success is any HTTP 2xx response.
 */
export const requestTrialSignup = async (
  values: TrialSignupFormValues,
  invite: TrialInviteCredentials,
  signal?: AbortSignal
): Promise<TrialRegisterResponse> => {
  const fallbackMessage = TRIAL_SIGNUP.ERRORS.REGISTER_FAILED;
  const payload = toTrialRegisterPayload(values, invite);
  const url = `${API.BASE_URL}${TRIAL_SIGNUP_ENDPOINTS.REGISTER}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw new TrialSignupApiError(fallbackMessage);
  }

  let data: unknown = {};
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new TrialSignupApiError(fallbackMessage);
    }
    // 2xx with empty / non-JSON body is still success
  }

  if (!response.ok) {
    throw new TrialSignupApiError(readErrorMessage(data) || fallbackMessage);
  }

  return asTrialRegisterResponse(data);
};
